// =====================================================================================
// INVRT Radar — web console — prospecting (classic mode)
//
// Search is free and returns previews with no contact detail — that is what the reveal is
// paid for. The route to spending anything runs strictly:
//   select rows → request a quote (free) → a person approves the named cost → reveal.
//
// The quote and approve steps live in useRevealFlow + QuoteApproveCard, shared verbatim
// with the chat panel, so the spend gate is one implementation rather than two.
// =====================================================================================

import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Search as SearchIcon } from "lucide-react";
import { useApiClient, useBalance } from "@/hooks/useApi";
import { useRevealFlow } from "@/hooks/useRevealFlow";
import { FacetField, splitList } from "@/components/FacetField";
import { QuoteApproveCard } from "@/components/QuoteApproveCard";
import { RevealedContacts } from "@/components/RevealedContacts";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input, Select } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { pluralise } from "@/lib/format";
import type { ProspectingFilters, SearchResponse } from "@/lib/types";

interface FormState {
  titles: string;
  levels: string[];
  industries: string[];
  technologies: string[];
  countries: string;
  states: string;
  keywords: string;
  employeesMin: string;
  employeesMax: string;
  emailQuality: "any" | "present" | "verified";
  perPage: string;
}

const INITIAL_FORM: FormState = {
  titles: "",
  levels: [],
  industries: [],
  technologies: [],
  countries: "",
  states: "",
  keywords: "",
  employeesMin: "",
  employeesMax: "",
  emailQuality: "any",
  perPage: "25",
};

function toFilters(form: FormState): ProspectingFilters {
  const titles = splitList(form.titles);
  const countries = splitList(form.countries);
  const states = splitList(form.states);
  const keywords = splitList(form.keywords);
  const min = Number.parseInt(form.employeesMin, 10);
  const max = Number.parseInt(form.employeesMax, 10);

  const employees = {
    ...(Number.isFinite(min) ? { min } : {}),
    ...(Number.isFinite(max) ? { max } : {}),
  };

  return {
    ...(titles.length > 0 ? { titles } : {}),
    ...(form.levels.length > 0 ? { levels: form.levels } : {}),
    ...(form.industries.length > 0 ? { industries: { linkedin: form.industries } } : {}),
    ...(form.technologies.length > 0 || keywords.length > 0 || Object.keys(employees).length > 0
      ? {
          company: {
            ...(form.technologies.length > 0 ? { technologies: form.technologies } : {}),
            ...(keywords.length > 0 ? { keywords } : {}),
            ...(Object.keys(employees).length > 0 ? { employees } : {}),
          },
        }
      : {}),
    ...(countries.length > 0 || states.length > 0
      ? {
          location: {
            ...(countries.length > 0 ? { countries } : {}),
            ...(states.length > 0 ? { states } : {}),
          },
        }
      : {}),
    ...(form.emailQuality === "any" ? {} : { quality: { email: form.emailQuality } }),
  };
}

export function ProspectingPage() {
  const api = useApiClient();
  const balance = useBalance();
  const reveal = useRevealFlow();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // The zero-match empty state hands the keyboard back to the filters form through this
  // ref on its first field: the empty state's own advice (widen the criteria) becomes the
  // action, so the state is never a dead end.
  const firstFilterFieldRef = useRef<HTMLInputElement>(null);

  const search = useMutation({
    mutationFn: (input: { filters: ProspectingFilters; page: number; perPage: number }) =>
      api.post<SearchResponse>("/leads/search", {
        filters: input.filters,
        page: input.page,
        per_page: input.perPage,
      }),
  });

  function runSearch(nextPage: number): void {
    const perPage = Number.parseInt(form.perPage, 10);
    setPage(nextPage);
    setSelected(new Set());
    reveal.reset();
    search.mutate({
      filters: toFilters(form),
      page: nextPage,
      perPage: Number.isFinite(perPage) ? perPage : 25,
    });
  }

  function toggleRow(ref: string): void {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  }

  const rows = search.data?.rows ?? [];
  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.provider_ref));

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold text-ink">Find people</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Searching and previewing cost nothing. You only spend credits when you reveal a
          contact&rsquo;s details, and only after you approve the exact figure.
        </p>
      </div>

      <Card>
        <CardHeader>
          <h2 className="text-base font-semibold text-ink">Filters</h2>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault();
              runSearch(1);
            }}
          >
            <Input
              ref={firstFilterFieldRef}
              label="Job titles"
              value={form.titles}
              onChange={(event) => setForm({ ...form, titles: event.target.value })}
              placeholder="Head of Compliance, Finance Director"
              hint="Separate several with commas."
            />

            <FacetField
              kind="departments_levels"
              label="Seniority and function"
              value={form.levels}
              onChange={(levels) => setForm({ ...form, levels })}
            />

            <FacetField
              kind="industries"
              label="Industries"
              value={form.industries}
              onChange={(industries) => setForm({ ...form, industries })}
            />

            <FacetField
              kind="technologies"
              label="Technologies in use"
              value={form.technologies}
              onChange={(technologies) => setForm({ ...form, technologies })}
            />

            <Input
              label="Countries"
              value={form.countries}
              onChange={(event) => setForm({ ...form, countries: event.target.value })}
              placeholder="United Kingdom, Ireland"
              hint="Separate several with commas."
            />

            <Input
              label="States or regions"
              value={form.states}
              onChange={(event) => setForm({ ...form, states: event.target.value })}
              placeholder="Greater London"
              hint="Separate several with commas."
            />

            <Input
              label="Company keywords"
              value={form.keywords}
              onChange={(event) => setForm({ ...form, keywords: event.target.value })}
              placeholder="payments, fintech"
              hint="Separate several with commas."
            />

            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Smallest company"
                type="number"
                min={0}
                value={form.employeesMin}
                onChange={(event) => setForm({ ...form, employeesMin: event.target.value })}
                placeholder="10"
                hint="Employees."
              />
              <Input
                label="Largest company"
                type="number"
                min={0}
                value={form.employeesMax}
                onChange={(event) => setForm({ ...form, employeesMax: event.target.value })}
                placeholder="500"
                hint="Employees."
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Email quality"
                value={form.emailQuality}
                onChange={(event) =>
                  setForm({
                    ...form,
                    emailQuality: event.target.value as FormState["emailQuality"],
                  })
                }
              >
                <option value="any">Any</option>
                <option value="present">Has an email</option>
                <option value="verified">Verified email</option>
              </Select>
              <Select
                label="Results per page"
                value={form.perPage}
                onChange={(event) => setForm({ ...form, perPage: event.target.value })}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Select>
            </div>

            <div className="flex items-end gap-3 md:col-span-2 lg:col-span-3">
              <Button type="submit" loading={search.isPending}>
                <SearchIcon className="h-4 w-4" aria-hidden="true" />
                Search
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setForm(INITIAL_FORM);
                  search.reset();
                  setSelected(new Set());
                  reveal.reset();
                }}
              >
                Clear filters
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {search.isPending ? <LoadingState label="Searching…" /> : null}
      {search.isError ? (
        <ErrorState error={search.error} onRetry={() => runSearch(page)} />
      ) : null}

      {search.isSuccess ? (
        <Card>
          <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h2 className="text-base font-semibold text-ink">
                {search.data.total.toLocaleString("en-GB")} matches, page {search.data.page}
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                {search.data.suppressed_count > 0 ? (
                  <Badge tone="success">
                    {pluralise(search.data.suppressed_count, "contact")} hidden — already yours
                  </Badge>
                ) : null}
                {search.data.unmapped.length > 0 ? (
                  <Badge tone="warning">
                    {pluralise(search.data.unmapped.length, "filter")} could not be applied
                  </Badge>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-ink-muted">
                {selected.size === 0
                  ? "Nothing selected"
                  : `${pluralise(selected.size, "contact")} selected`}
              </span>
              <Button
                disabled={selected.size === 0 || reveal.phase === "quoting"}
                loading={reveal.phase === "quoting"}
                onClick={() => void reveal.quoteFor([...selected])}
              >
                Get a price
              </Button>
            </div>
          </CardHeader>

          <CardContent className="flex flex-col gap-4">
            {search.data.unmapped.length > 0 ? (
              <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-warning">
                <p className="font-medium">These filters were not applied:</p>
                <ul className="mt-1 list-inside list-disc">
                  {search.data.unmapped.map((entry) => (
                    <li key={entry.field}>
                      {entry.field} — {entry.reason}
                    </li>
                  ))}
                </ul>
                <p className="mt-1">The rest of your search still ran.</p>
              </div>
            ) : null}

            {rows.length === 0 ? (
              <EmptyState
                title="No matches for those filters"
                body="Widen the criteria — fewer titles, a broader location, or a wider company size — and search again. Searching costs nothing."
                action={
                  <Button
                    variant="secondary"
                    onClick={() => firstFilterFieldRef.current?.focus()}
                  >
                    Refine your filters
                  </Button>
                }
              />
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell className="w-10">
                        <label className="sr-only" htmlFor="select-all-results">
                          Select every result on this page
                        </label>
                        <input
                          id="select-all-results"
                          type="checkbox"
                          checked={allSelected}
                          onChange={() =>
                            setSelected(
                              allSelected
                                ? new Set()
                                : new Set(rows.map((row) => row.provider_ref)),
                            )
                          }
                        />
                      </TableHeaderCell>
                      <TableHeaderCell>Title</TableHeaderCell>
                      <TableHeaderCell>Company</TableHeaderCell>
                      <TableHeaderCell>Location</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.provider_ref}>
                        <TableCell>
                          <label className="sr-only" htmlFor={`select-${row.provider_ref}`}>
                            Select {row.title ?? row.provider_ref}
                          </label>
                          <input
                            id={`select-${row.provider_ref}`}
                            type="checkbox"
                            checked={selected.has(row.provider_ref)}
                            onChange={() => toggleRow(row.provider_ref)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{row.title ?? "—"}</TableCell>
                        <TableCell className="text-ink-muted">
                          {row.company_name ?? "—"}
                        </TableCell>
                        <TableCell className="text-ink-muted">{row.location ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="flex items-center justify-between">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => runSearch(page - 1)}
                  >
                    Previous page
                  </Button>
                  <span className="text-xs text-ink-muted">
                    Names and contact details stay hidden until you reveal them.
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={rows.length < search.data.page_size}
                    onClick={() => runSearch(page + 1)}
                  >
                    Next page
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ) : null}

      {reveal.phase === "failed" && reveal.error ? (
        <ErrorState error={reveal.error} onRetry={() => void reveal.quoteFor([...selected])} />
      ) : null}

      {reveal.quote && (reveal.phase === "awaiting_approval" || reveal.phase === "revealing") ? (
        <QuoteApproveCard
          quote={reveal.quote}
          balance={balance.data?.balance ?? null}
          approving={reveal.phase === "revealing"}
          onApprove={() => void reveal.approve()}
          onCancel={reveal.cancel}
          {...(reveal.error ? { error: reveal.error } : {})}
        />
      ) : null}

      {reveal.result ? <RevealedContacts result={reveal.result} /> : null}
    </div>
  );
}
